import styled, { keyframes } from "styled-components";

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const Container = styled.section`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
  height: 100%;
  background: var(--background);
`;

export const PageHeader = styled.div`
  background: var(--surface);
  padding: 105px 0 30px;
  text-align: center;
  border-bottom: 2px solid var(--accent-light);

  h1 {
    font-size: 48px;
    font-weight: 700;
    margin-bottom: 20px;
    text-transform: uppercase;
    letter-spacing: 2px;
    background: var(--gradient-primary);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;

    @media (max-width: 768px) {
      font-size: 36px;
    }
  }

  p {
    font-size: 16px;
    color: var(--text-muted);
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.8;
  }
  @media (max-width: 920px) {
    padding: 102.3px 0 30px;
  }
`;

export const ContentSection = styled.div`
  padding: 60px 40px;
  max-width: 1000px;
  margin: 0 auto;
  width: 100%;
  animation: ${fadeInUp} 0.8s ease-out 0.2s both;

  @media (max-width: 768px) {
    padding: 40px 20px;
  }
`;

export const SearchSection = styled.div`
  margin-bottom: 40px;
  text-align: center;
`;

export const SearchInput = styled.input`
  width: 100%;
  max-width: 500px;
  padding: 15px 20px;
  border: 2px solid var(--accent-light);
  border-radius: 25px;
  font-size: 16px;
  font-family: "Source Code Pro", monospace;
  background: var(--surface);
  color: var(--text-primary);
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(232, 165, 165, 0.1);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

export const CategoryTabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  margin-bottom: 40px;
  animation: ${fadeInUp} 0.8s ease-out 0.4s both;
`;

export const CategoryTab = styled.button`
  padding: 12px 25px;
  border: 2px solid
    ${(props) => (props.$active ? "var(--accent)" : "var(--accent-light)")};
  background: ${(props) =>
    props.$active ? "var(--accent)" : "var(--surface)"};
  color: ${(props) => (props.$active ? "var(--surface)" : "var(--accent)")};
  border-radius: 25px;
  font-weight: 600;
  font-family: "Source Code Pro", monospace;
  text-transform: uppercase;
  font-size: 12px;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: var(--accent);
    color: var(--surface);
    border-color: var(--accent);
    transform: translateY(-2px);
    box-shadow: var(--shadow-soft);
  }

  @media (max-width: 768px) {
    padding: 10px 20px;
    font-size: 11px;
  }
`;

export const FAQContainer = styled.div`
  display: grid;
  gap: 20px;
`;

export const FAQItem = styled.div`
  background: var(--surface);
  border: 2px solid var(--accent-light);
  border-radius: 20px;
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: var(--shadow-soft);

    transform: translateY(-2px);
  }
`;

export const FAQQuestion = styled.div`
  padding: 25px 30px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(232, 165, 165, 0.05);
  }

  h3 {
    font-size: 18px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0;
    flex: 1;
    text-align: left;
  }

  .icon {
    font-size: 20px;
    color: var(--accent);
    transition: transform 0.3s ease;
    transform: ${(props) => (props.$isOpen ? "rotate(45deg)" : "rotate(0deg)")};
  }

  @media (max-width: 768px) {
    padding: 20px;

    h3 {
      font-size: 16px;
    }
  }
`;

export const FAQAnswer = styled.div`
  max-height: ${(props) => (props.$isOpen ? "500px" : "0")};
  overflow: hidden;
  transition: max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border-top: ${(props) =>
    props.$isOpen ? "1px solid var(--accent-light)" : "1px solid transparent"};

  .answer-content {
    padding: ${(props) => (props.$isOpen ? "25px 30px" : "0 30px")};
    color: var(--text-secondary);
    line-height: 1.7;
    font-size: 16px;
    transition: padding 0.4s cubic-bezier(0.4, 0, 0.2, 1);

    p {
      margin-bottom: 15px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    ul,
    ol {
      padding-left: 20px;
      margin-bottom: 15px;

      li {
        margin-bottom: 8px;
      }
    }

    strong {
      color: var(--text-primary);
    }

    @media (max-width: 768px) {
      padding: ${(props) => (props.$isOpen ? "20px" : "0 20px")};
      font-size: 14px;
    }
  }
`;

export const CategorySection = styled.div`
  margin-bottom: 40px;
  transition: all 0.3s ease;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const CategoryTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 25px;
  text-transform: uppercase;
  color: var(--text-primary);
  position: relative;
  padding-bottom: 15px;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 50px;
    height: 3px;
    background: var(--gradient-primary);
  }
`;

export const NoResults = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: var(--text-muted);

  .icon {
    font-size: 64px;
    color: var(--accent-light);
    margin-bottom: 20px;
  }

  h3 {
    font-size: 24px;
    color: var(--text-secondary);
    margin-bottom: 15px;
  }

  p {
    font-size: 16px;
    line-height: 1.6;
  }
`;

export const ContactCTA = styled.div`
  background: var(--surface);
  border: 2px solid var(--accent-light);
  border-radius: 20px;
  padding: 40px;
  text-align: center;
  margin-top: 60px;

  h3 {
    font-size: 24px;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 15px;
  }

  p {
    font-size: 16px;
    color: var(--text-secondary);
    margin-bottom: 25px;
    line-height: 1.6;
  }

  .contact-button {
    background: var(--gradient-primary);
    color: var(--surface);

    padding: 10px 30px;
    border: none;
    border-radius: 30px;
    font-weight: 600;
    font-family: "Source Code Pro", monospace;
    text-decoration: none;
    transition:
      color 0.3s ease,
      box-shadow 0.3s ease,
      transform 0.3s ease;

    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    text-transform: uppercase;
    font-size: 12px;
    letter-spacing: 1px;
    box-shadow: var(--shadow-soft);

    &:hover {
      transform: translateY(-3px);
      box-shadow: var(--shadow-medium);
      background: var(--accent-dark);
      color: var(--surface);
    }
  }
`;
